import "@arco-design/web-react/es/Space/style";
import _Space from "@arco-design/web-react/es/Space";
import "@arco-design/web-react/es/Link/style";
import _Link from "@arco-design/web-react/es/Link";
import "@arco-design/web-react/es/Checkbox/style";
import _Checkbox from "@arco-design/web-react/es/Checkbox";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import _IconRefresh from "@arco-design/web-react/icon/react-icon/IconRefresh";
import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
import _IconLock from "@arco-design/web-react/icon/react-icon/IconLock";
import "@arco-design/web-react/es/Form/style";
import _Form from "@arco-design/web-react/es/Form";
import "@arco-design/web-react/es/Input/style";
import _Input from "@arco-design/web-react/es/Input";
import _IconUser from "@arco-design/web-react/icon/react-icon/IconUser";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useStorage from "@/utils/useStorage";
import useLocale from "@/utils/useLocale";
import request from "@/utils/request";
import { getAuthToken, setAuthToken } from "@/utils/token";
import locale from "./locale";
import styles from "./style/index.module.less";
export default function LoginForm() {
  const formRef = useRef();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginParams, setLoginParams, removeLoginParams] = useStorage("loginParams");
  const removeLoginParamsRef = useRef(removeLoginParams);
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaKey, setCaptchaKey] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const t = useLocale(locale);
  const [rememberPassword, setRememberPassword] = useState(!!loginParams);
  const clearCaptchaInput = useCallback(() => {
    setCaptchaInput("");
    formRef.current?.setFieldsValue({
      captcha: ""
    });
  }, []);
  const fetchCaptcha = useCallback(async (clearInput = true) => {
    setCaptchaLoading(true);
    setErrorMessage("");

    try {
      const res = await request.get("/admin/captcha");
      const {
        code,
        data,
        message
      } = res;

      if (code === 200 && data?.captcha && data?.key) {
        setCaptchaImage(data.captcha);
        setCaptchaKey(data.key);

        if (clearInput) {
          clearCaptchaInput();
        }
      } else {
        throw new Error(message || t["login.form.captcha.errMsg"]);
      }
    } catch (error) {
      setErrorMessage(error.message || t["login.form.captcha.errMsg"]);
    } finally {
      setCaptchaLoading(false);
    }
  }, [clearCaptchaInput, t]);

  const isCaptchaError = message => {
    if (!message) {
      return false;
    }

    if (message.includes("验证码")) {
      return true;
    }

    return message.toLowerCase().includes("captcha");
  };

  function afterLoginSuccess(params) {
    // 记住密码
    if (rememberPassword) {
      const {
        username,
        password
      } = params;
      setLoginParams(JSON.stringify({
        username,
        password
      }));
    } else {
      removeLoginParams();
    } // 记录登录状态


    localStorage.setItem("userStatus", "login"); // 跳转首页

    window.location.href = "/";
  }

  async function login(params) {
    setErrorMessage("");
    setLoading(true);

    try {
      if (!captchaKey) {
        _Message.error(t["login.form.captcha.errMsg"]);

        fetchCaptcha();
        return;
      }

      const captchaValue = (params?.captcha ?? captchaInput ?? formRef.current?.getFieldValue("captcha"))?.trim() || "";

      if (!captchaValue) {
        _Message.error(t["login.form.captcha.errMsg"]);

        return;
      }

      const payload = { ...params,
        captcha: captchaValue,
        key: captchaKey,
        remember: rememberPassword
      };
      const res = await request.post("/admin/login", payload);
      const {
        code,
        message,
        data
      } = res;

      if (code === 200 && data?.token) {
        const authToken = `${data.head || ""}${data.token}`;
        setAuthToken(authToken);
        afterLoginSuccess(params);
      } else {
        const fallbackMessage = message || t["login.form.login.errMsg"];
        setErrorMessage(fallbackMessage);

        _Message.error(fallbackMessage);

        if (isCaptchaError(message)) {
          clearCaptchaInput();
          fetchCaptcha();
        } else {
          fetchCaptcha(false);
        }
      }
    } catch (error) {
      setErrorMessage(t["login.form.login.errMsg"]);
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  }

  function onSubmitClick() {
    formRef.current.validate().then(values => {
      login(values);
    });
  } // 读取 localStorage，设置初始值


  useEffect(() => {
    removeLoginParamsRef.current = removeLoginParams;
  }, [removeLoginParams]);
  useEffect(() => {
    if (loginParams) {
      try {
        const parseParams = JSON.parse(loginParams);
        formRef.current?.setFieldsValue({
          username: parseParams.username,
          password: parseParams.password
        });
        setRememberPassword(true);
      } catch (error) {
        removeLoginParamsRef.current?.();
      }
    } else {
      setRememberPassword(false);
    }
  }, [loginParams]);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const userStatus = window.localStorage.getItem("userStatus");

    if (getAuthToken() && userStatus === "login") {
      window.location.replace("/");
      return;
    }

    fetchCaptcha();
  }, [fetchCaptcha]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-wrapper"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-title"]
  }, t["login.form.title"]), /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-sub-title"]
  }, t["login.form.title"]), /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-error-msg"]
  }, errorMessage), /*#__PURE__*/React.createElement(_Form, {
    className: styles["login-form"],
    layout: "vertical",
    ref: formRef,
    initialValues: {
      username: "",
      password: "",
      captcha: ""
    }
  }, /*#__PURE__*/React.createElement(_Form.Item, {
    field: "username",
    rules: [{
      required: true,
      message: t["login.form.userName.errMsg"]
    }]
  }, /*#__PURE__*/React.createElement(_Input, {
    prefix: /*#__PURE__*/React.createElement(_IconUser, null),
    placeholder: t["login.form.userName.placeholder"],
    onPressEnter: onSubmitClick
  })), /*#__PURE__*/React.createElement(_Form.Item, {
    field: "password",
    rules: [{
      required: true,
      message: t["login.form.password.errMsg"]
    }]
  }, /*#__PURE__*/React.createElement(_Input.Password, {
    prefix: /*#__PURE__*/React.createElement(_IconLock, null),
    placeholder: t["login.form.password.placeholder"],
    onPressEnter: onSubmitClick
  })), /*#__PURE__*/React.createElement(_Form.Item, {
    field: "captcha",
    rules: [{
      required: true,
      message: t["login.form.captcha.errMsg"]
    }]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-captcha"]
  }, /*#__PURE__*/React.createElement(_Input, {
    placeholder: t["login.form.captcha.placeholder"],
    onPressEnter: onSubmitClick,
    maxLength: 6,
    value: captchaInput,
    onChange: value => {
      setCaptchaInput(value);
      formRef.current?.setFieldsValue({
        captcha: value
      });
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-captcha-img"]
  }, captchaLoading ? /*#__PURE__*/React.createElement(_Spin, null) : captchaImage ? /*#__PURE__*/React.createElement("img", {
    src: captchaImage,
    alt: t["login.form.captcha.alt"],
    onClick: fetchCaptcha
  }) : /*#__PURE__*/React.createElement(_Button, {
    type: "text",
    size: "mini",
    icon: /*#__PURE__*/React.createElement(_IconRefresh, null),
    onClick: fetchCaptcha
  }, t["login.form.refreshCaptcha"]))), /*#__PURE__*/React.createElement(_Button, {
    type: "text",
    size: "mini",
    className: styles["login-form-captcha-refresh"],
    icon: /*#__PURE__*/React.createElement(_IconRefresh, null),
    onClick: fetchCaptcha
  }, t["login.form.refreshCaptcha"])), /*#__PURE__*/React.createElement(_Space, {
    size: 16,
    direction: "vertical"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["login-form-password-actions"]
  }, /*#__PURE__*/React.createElement(_Checkbox, {
    checked: rememberPassword,
    onChange: setRememberPassword
  }, t["login.form.rememberPassword"]), /*#__PURE__*/React.createElement(_Link, {
    onClick: () => _Message.info(t["login.form.forgetPassword.tip"] || "请联系管理员")
  }, t["login.form.forgetPassword"])), /*#__PURE__*/React.createElement(_Button, {
    type: "primary",
    long: true,
    onClick: onSubmitClick,
    loading: loading
  }, t["login.form.login"]))));
}